<template>
  <v-dialog
    v-if="dialogAttachmentType !== null"
    v-model="modalState"
    width="800"
    content-class="audio-modal"
    persistent
  >
    <v-card
      class="d-flex flex-column"
      style="min-height: 675px"
    >
      <v-card-title
        v-if="!isFileSelected"
        class="pb-5 flex-shrink-0"
      >
        <btn
          label="Close"
          :icon="icons.mdiClose"
          color="secondary"
          @click="closeModal"
        ></btn>
        <v-spacer></v-spacer>
        AI Dictation
        <v-spacer></v-spacer>

        <btn
          v-if="!isRecording"
          label="Record Audio"
          :icon="$store.getters['baseData/bucketTypeFromValue'](dialogAttachmentType).icon"
          :disabled="signed"
          @click="startRecording"
        ></btn>
        <btn
          v-else
          label="Stop Recording"
          :icon="icons.mdiStopCircle"
          color="error"
          @click="stopRecording"
        ></btn>

        <btn
          v-if="!isRecording"
          label="Select Audio File"
          :icon="icons.mdiFileMusic"
          :disabled="signed"
          @click="selectAttachmentClicked"
        ></btn>
        <btn
          v-else-if="isRecording && !isPaused"
          label="Pause Recording"
          :icon="icons.mdiPauseCircle"
          @click="pauseRecording"
        ></btn>
        <btn
          v-else
          label="Resume Recording"
          :icon="icons.mdiPlayCircle"
          color="success"
          @click="resumeRecording"
        ></btn>

        <input
          ref="uploader"
          type="file"
          class="d-none"
          accept=".x-m4a, .m4a, .aac, .mp3, .wav, .mp4, .ogg, .x-aiff, .flac"
          @change="onFileChanged"
        />
      </v-card-title>

      <v-card-text class="pa-0 flex-grow-1 overflow-y-auto">
        <!-- Tips section -->
        <v-expansion-panels flat>
          <v-expansion-panel>
            <v-expansion-panel-header>
              <span class="font-weight-medium primary--text d-flex align-center">
                <v-icon small color="primary" class="mr-2">
                  {{ icons.mdiInformationOutline }}
                </v-icon>
                Tips for Recording Audio
              </span>
            </v-expansion-panel-header>

            <v-expansion-panel-content>
              <p>
                For the best dictation results, follow this format:
              </p>

              <ol>
                <li><strong>Encounter-level details</strong></li>
                <li>
                  <strong>Wound location A treatment</strong>
                  <ul>
                    <li>Wound location A treatment details</li>
                  </ul>
                </li>
                <li>
                  <strong>Wound location B treatment</strong>
                  <ul>
                    <li>Wound location B treatment details</li>
                  </ul>
                </li>
                <li>Continue for each wound location.</li>
              </ol>

              <p class="mb-2"><strong>Tips:</strong></p>

              <ul>
                <li>
                  Don't dictate punctuation such as
                  <strong>"period"</strong> or <strong>"comma"</strong>.
                </li>
                <li>
                  Say the <strong>section or field name</strong> before the information
                  you want to enter.
                </li>
                <li>
                  For example, say
                  <strong>"goals: wound stabilization"</strong>
                  instead of just
                  <strong>"wound stabilization"</strong>.
                </li>
              </ul>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <!-- Data table -->
        <v-data-table
          :headers="headers"
          :items="currentAttachmentsArr()"
          :class="isFileSelected && 'disable'"
          item-key="id"
          sort-by="created"
          :sort-desc="true"
          disable-pagination
          hide-default-footer
          @click:row="viewAttachment"
        >
          <template #item.data="{ item }">
            <btn
              v-if="!item.data || item.data === 'placeholder'"
              label="Click to download"
              color="primary"
              :icon="icons.mdiDownload"
              offline-disable
              @click.stop="viewAttachment(item)"
            ></btn>
            <audio
              v-else
              :src="item.data"
              controls
              controlslist="nodownload"
              @error="handleAudioError(item)"
            ></audio>
          </template>

          <template #item.created="{ item }">
            {{ formatDateTime(item.created) }}
          </template>

          <template #item.size="{ item }">
            {{ Math.round(item.attachment_filesize / 1024) }}KB
          </template>

          <template #item.updated="{ item }">
            <v-icon
              v-if="item.updated && item.s3_upload_status !== 'UPLOADED_READY_FOR_AI_DB'"
              right
              color="error"
              @click.stop="deleteAttachment(null, item)"
            >
              {{ icons.mdiTrashCan }}
            </v-icon>
            <v-icon
              v-else
              right
              color="success"
              @click.stop="viewAttachment(item)"
            >
              {{ icons.mdiCloudCheckVariant }}
            </v-icon>
          </template>
        </v-data-table>
      </v-card-text>

      <v-card-actions class="d-flex justify-end flex-shrink-0 pa-3">
        <btn
          v-if="hasExistingAttachment"
          label="Begin AI Processing"
          :icon="icons.mdiCloudUpload"
          color="success"
          large
          @click="beginAIProcessing"
        ></btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>

import { encounterRecordings } from '@/store'
import { getEncryptionKey, encryptBytes, decryptBytes } from '@/plugins/storage-encryption'
import { sleep } from '@/utils/sleep'
import {
  mdiClose,
  mdiCloudCheckVariant,
  mdiCloudUpload,
  mdiDownload,
  mdiFileMusic,
  mdiPauseCircle,
  mdiPlayCircle,
  mdiPlayCircleOutline,
  mdiStopCircle,
  mdiTrashCan,
  mdiInformationOutline
} from '@mdi/js'
import { v4 as uuidv4 } from 'uuid'
import Vue from 'vue'
import { mapGetters } from 'vuex'
import HelpIcon from '@/components/elements/HelpIcon.vue'

export default {
  components: { HelpIcon },
  props: {
    audioRecorderModal: {
      type: Boolean,
      default: false,
    },
    attachmentTypeId: {
      type: [Number, String],
      default: null,
    },
    attachmentType: {
      type: String,
      default: null,
    },
    patientId: {
      type: String,
      default: null,
    },
    encounterAttachments: {
      type: Array,
      default: [],
    },
    signed: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      formValid: false,
      modalState: false,
      dialogAttachmentTypeId: null,
      dialogAttachmentType: null,
      encounterId: this.$route.query.id,
      isFileSelected: false,
      file: [],
      attachment: {
        attachment_filename: 'dictation',
        attachment_mime_type: null,
        attachment_filepath: '',
        attachment_width: 0,
        attachment_height: 0,
        s3_upload_status: '',
        data: null,
      },
      uploadProgress: 0,
      downloadedAttachments: [],
      fileDescription: null,
      isRecording: false,
      isPaused: false,
      chunksSent: 0,
      pendingChunkWrites: [],
      recordingIncomplete: false,
      pendingOfflineSession: null,
      isSyncingPending: false,
      mediaRecorder: null,
      recordingMimeType: null,
      icons: {
        mdiClose,
        mdiTrashCan,
        mdiCloudCheckVariant,
        mdiDownload,
        mdiPlayCircleOutline,
        mdiFileMusic,
        mdiCloudUpload,
        mdiStopCircle,
        mdiPauseCircle,
        mdiPlayCircle,
        mdiInformationOutline,
      },
      headers: [
        {
          text: 'Audio',
          value: 'data',
          align: 'center',
          sortable: false,
          width: 250,
          filter: value => !this.data || value === this.data,
        },
        {
          text: 'Description',
          value: 'attachment_filename',
          align: 'center',
          width: 200,
        },
        {
          text: 'Created',
          value: 'created',
          align: 'center',
        },
        {
          text: 'Size',
          value: 'size',
          sortable: false,
        },
        {
          text: 'Synced',
          value: 'updated',
          align: 'small',
        },
        {
          text: 'Bucket',
          value: 'attachment_type',
          align: 'hide',
          filter: value => !this.dialogAttachmentType || value === this.dialogAttachmentType,
        },
      ],
    }
  },
  computed: {
    ...mapGetters(['isOnline']),
    hasExistingAttachment() {
      return this.currentAttachmentsArr().length > 0
    },
  },
  watch: {
    modalState(n) {
      this.$emit('update:audio-recorder-modal', n)
    },
    audioRecorderModal() {
      this.modalState = this.audioRecorderModal
      this.dialogAttachmentTypeId = Number(this.attachmentTypeId)
      this.$emit('update:attachmentTypeId', null)
    },
    attachmentType() {
      this.dialogAttachmentType = this.attachmentType
    },
    encounterAttachments: {
      deep: true,
      handler() {
        if (this.downloadedAttachments.length > 0) {
          // Add any locally created attachments
          this.encounterAttachments.forEach(attachment => {
            if (attachment.updated && !this.downloadedAttachments.find(e => e.id === attachment.id)) {
              this.downloadedAttachments.push(attachment)
            }
          })

          // Remove attachment from downloadedAttachments if removed from encounterAttachments
          this.downloadedAttachments.forEach((attachment, attachmentIndex) => {
            const index = this.encounterAttachments.findIndex(e => e.id === attachment.id)

            // Only check locally created attachments via the 'updated' property.
            if (attachment.updated && index === -1) {
              this.downloadedAttachments.splice(attachmentIndex, 1)
            }
          })
        }
      },
    },
  },
  async mounted() {
    window.addEventListener('online', this.processPendingUploads)

    if (this.isOnline) {
      await this.processPendingUploads()
    }
  },
  beforeDestroy() {
    window.removeEventListener('online', this.processPendingUploads)
  },
  methods: {
    formatDate(dateTime) {
      return this.$date(dateTime).format('M/D/YYYY')
    },
    formatDateTime(dateTime) {
      return this.$date(dateTime).format('M/D/YYYY h:mm:ss A')
    },
    mimeType(mime) {
      return mime.substring(mime.indexOf('/') + 1).toUpperCase()
    },
    viewAttachment(item) {
      if (item.data === 'placeholder') {
        if (this.isOnline) {
          try {
            this.downloadAudioAttachment(item)
          } catch (e) {
            this.$store.dispatch('notify', { value: `Issue with retrieving attachment URL parameters. ${e}`, color: 'error' })
          }
        } else {
          this.$store.dispatch('notify', { value: 'Please connect to the internet to view attachment.', color: 'error' })
        }
      }
    },
    deleteAttachment(index, item = null) {
      this.$root.confirm({
        title: 'Delete Audio File?',
        body: 'Are you sure you wish to delete this audio attachment?',
        cancel: 'No',
        confirm: 'Delete',
        confirmIcon: this.icons.mdiTrashCan,
        confirmColor: 'error',
      }).then(result => {
        if (result) {
          if (index === null) index = this.encounterAttachments.findIndex(x => x.id === item.id)
          if (index !== -1) {
            this.encounterAttachments.splice(index, 1)
          }
          this.updateAttachments()
        }
      })
    },
    handleAudioError(item) {
      // Audio attachment src url expired, restore attachment.data to 'placeholder' to reload
      item.data = 'placeholder'
    },
    async selectAttachmentClicked() {
      if (this.hasExistingAttachment) {
        this.replaceAttachmentDialog()
      } else {
        this.$refs.uploader.click()
      }
    },
    async onFileChanged(e) {
      if (e.target.files.length > 0) {
        const selectedFile = e.target.files[0]

        if (selectedFile?.type.substring(0, 5) === 'audio') {
          this.file = selectedFile
          this.fileDescription = `visit-dictation/${this.encounterId}/dictation${this.mimeTypeToExtension(selectedFile.type)}`

          this.attachment = {
            ...this.attachment,
            encounter_id: this.encounterId,
            patient_id: this.patientId,
            attachment_type: this.dialogAttachmentType,
            attachment_mime_type: this.file.type,
            attachment_filename: 'dictation',
            attachment_filepath: this.fileDescription,
            attachment_filesize: this.file.size,
            data: 'placeholder',
          }
          this.isFileSelected = true
          await this.addFile()
        } else {
          this.$refs.uploader.value = null
          this.resetUploadValues()
          await this.$store.dispatch('notify', {
            value: `File must be an audio file, file type is: ${this.mimeType(selectedFile.type)}`,
            color: 'error',
          })
        }
      }
    },
    async addFile() {
      const isReplacing = this.hasExistingAttachment

      this.attachment = isReplacing ? {...this.currentAttachmentsArr()[0]} : {
        ...this.attachment,
        id: uuidv4(),
        updated: true,
      }

      this.attachment.created = this.$custom.utcNow()
      this.attachment.attachment_filepath = this.fileDescription
      this.attachment.attachment_mime_type = this.file.type
      this.attachment.attachment_filesize = this.file.size

      await this.$store.dispatch('encounters/syncingModal', true)

      try {
        // The selected file on the user's device uploads straight to S3 if the user is online
        if (!this.isOnline) throw new Error('offline')

        const presigned = await this.$store.dispatch('patients/getPresignedUrl', {
          urlString: this.attachment.attachment_filepath,
          method: 'PutObject',
          seconds: 1800,
        })

        if (!presigned || presigned.status !== 'success') throw new Error('Unable to get S3 upload URL')

        const putResult = await this.$store.dispatch('patients/putPatientAttachmentToS3', {
          url: presigned.data,
          file: this.file,
        })

        if (typeof putResult === 'string' && putResult.length > 0) throw new Error(putResult)

        // Update local attachment
        this.attachment.attachment_filesize = this.file.size
        this.attachment.s3_upload_status = 'UPLOADED_READY_FOR_AI'

        // The previous file stays intact until the replacement is confirmed in S3
        if (isReplacing) this.replaceAudioRecording(this.attachment)

        this.encounterAttachments.push({ ...this.attachment })
        this.updateAttachments()
        this.$store.dispatch('notify', { value: 'Audio file uploaded successfully.', color: 'success' })
      } catch (error) {
        const offline = error && error.message === 'offline'
        this.$store.dispatch('notify', {
          value: offline
            ? 'You are offline. Please reconnect and select the file again to upload.'
            : 'Failed to upload audio file.',
          color: 'error',
        })
      } finally {
        this.$store.dispatch('encounters/syncingModal', false)
        this.resetUploadValues()
      }
    },
    updateAttachments() {
      this.$store.commit('patients/updateAttachments', {
        id: this.patientId,
        attachments: this.encounterAttachments,
      })
    },
    async startRecording() {
      if (this.hasExistingAttachment) {
        const confirmed = await this.$root.confirm({
          title: 'Replace existing audio file?',
          body: 'Are you sure you want to proceed?',
          cancel: 'Cancel',
          confirm: 'Replace Audio',
          confirmColor: 'error',
        })

        if (!confirmed) return
      }

      this.attachment = this.hasExistingAttachment ? {...this.currentAttachmentsArr()[0]} : {
        ...this.attachment,
        id: uuidv4(),
        encounter_id: this.encounterId,
        patient_id: this.patientId,
        attachment_filename: 'dictation',
        attachment_type: this.dialogAttachmentType,
        attachment_filesize: 0,
        data: 'placeholder',
        updated: true,
        created: this.$custom.utcNow(),
      }

      if (this.hasExistingAttachment) this.attachment.toReplace = true

      // Get media stream & initialize MediaRecorder
      let stream
      try {
        stream = await navigator.mediaDevices.getUserMedia({audio: true})
      } catch (e) {
        this.$store.dispatch('notify', {
          value: 'Microphone access is required to record. Please allow access and try again.',
          color: 'error',
        })

        return
      }

      this.isRecording = true
      this.mediaRecorder = new MediaRecorder(stream)
      this.pendingChunkWrites = []
      this.recordingIncomplete = false
      this.recordingMimeType = 'audio/m4a'
      this.attachment.attachment_filepath = `visit-dictation/${this.encounterId}/dictation.m4a`
      this.attachment.attachment_mime_type = this.recordingMimeType

      try {
        // Session manifest, on stopRecording stitches chunks/24h sync cleans up abandoned recording
        await encounterRecordings.setItem(this.sessionMetaKey(this.encounterId), {
          sessionId: this.encounterId,
          encounterId: this.encounterId,
          patientId: this.patientId,
          status: 'recording',
          chunksSent: 0,
          recordingMimeType: this.recordingMimeType,
          attachment: {...this.attachment},
          createdAt: Date.now(),
        })
      } catch (error) {
        stream.getTracks().forEach(track => track.stop())
        this.isRecording = false
        this.mediaRecorder = null
        this.$store.dispatch('notify', { value: 'Unable to start recording (iDB error). If the problem persists, please contact support.', color: 'error' })

        return
      }


      this.mediaRecorder.ondataavailable = event => {
        if (event.data.size > 0) {
          const index = this.chunksSent
          this.chunksSent += 1
          this.pendingChunkWrites.push(this.persistChunk(event.data, index))
        }
      }

      this.mediaRecorder.start(5000)
    },
    async stopRecording() {
      if (this.mediaRecorder && this.isRecording) {
        if (this.mediaRecorder.state === 'paused') {
          this.mediaRecorder.resume()
        }

        this.isRecording = false
        this.isPaused = false

        const recorderStopped = new Promise((resolve, reject) => {
          this.mediaRecorder.onstop = resolve
          this.mediaRecorder.onerror = event => {
            reject(new Error(`Recorder failed: ${event?.error?.name || 'unknown error'}`))
          }
          setTimeout(() => reject(new Error('Recorder did not stop in time')), 10000)
        })

        this.mediaRecorder.stop()

        if (this.mediaRecorder.stream) {
          this.mediaRecorder.stream.getTracks().forEach(track => track.stop())
        }

        await this.$store.dispatch('encounters/syncingModal', true)

        try {
          await recorderStopped
          await Promise.all(this.pendingChunkWrites)

          if (this.chunksSent === 0) {
            this.$store.dispatch('notify', { value: 'Recording was too short to save.', color: 'error' })
            await this.deleteRecordingSession(this.encounterId)
            if (this.attachment.toReplace) this.attachment.toReplace = false

            return
          }

          // A chunk write failed, so this session can never be stitched into a complete file
          if (this.recordingIncomplete) throw new Error('incomplete')

          // Chunks remain in iDB, reattempt once reconnected or 24h cleanup handles it
          const sessionData = {
            sessionId: this.encounterId,
            chunksSent: this.chunksSent,
            recordingMimeType: this.recordingMimeType,
            attachment: {...this.attachment},
          }

          // If offline, persist the session to iDB
          if (!this.isOnline) {
            await this.savePendingUpload(sessionData)
            throw new Error('offline')
          }

          // Attempt online upload directly; on failure, persist for retry on reconnect
          try {
            await this.finalizeRecordingUpload(sessionData)
            this.$store.dispatch('notify', {
              value: 'Audio file uploaded successfully.',
              color: 'success'
            })
          } catch (uploadErr) {
            await this.savePendingUpload(sessionData)
            throw uploadErr
          }
        } catch (error) {
          let value = 'Upload failed. The recording is saved on-device.'

          if (error && error.message === 'offline') {
            value = 'You are offline. The recording is saved on-device and will upload on your next attempt.'
          } else if (error && error.message === 'incomplete') {
            value = 'Part of this recording could not be saved on this device, so it cannot be recovered. Please record the dictation again.'
            await this.deleteRecordingSession(this.encounterId)
          }
          this.$store.dispatch('notify', { value, color: 'error' })
        } finally {
          this.chunksSent = 0
          this.pendingChunkWrites = []
          this.recordingIncomplete = false
          this.mediaRecorder = null
          await this.$store.dispatch('encounters/syncingModal', false)
          this.resetUploadValues()
        }
      }
    },
    pauseRecording() {
      if (this.mediaRecorder && this.isRecording) {
        this.mediaRecorder.pause()
        this.isPaused = true
      }
    },
    resumeRecording() {
      if (this.mediaRecorder && this.isRecording) {
        this.mediaRecorder.resume()
        this.isPaused = false
      }
    },
    async finalizeRecordingUpload(session) {
      const {sessionId, chunksSent, recordingMimeType, attachment} = session

      // Sequentially read and decrypt chunks from iDB into one file
      const file = await this.stitchSessionChunks(sessionId, chunksSent, recordingMimeType)

      // Presigned PUT straight to S3
      const presigned = await this.$store.dispatch('patients/getPresignedUrl', {
        urlString: attachment.attachment_filepath,
        method: 'PutObject',
        seconds: 1800,
      })

      if (!presigned || presigned.status !== 'success') throw new Error('Unable to get upload URL')

      const putResult = await this.$store.dispatch('patients/putPatientAttachmentToS3', {
        url: presigned.data,
        file,
      })

      if (typeof putResult === 'string' && putResult.length > 0) throw new Error(putResult)

      attachment.attachment_filesize = file.size
      attachment.s3_upload_status = 'UPLOADED_READY_FOR_AI'

      // Discard the previously uploaded recording only now that the replacement is confirmed
      if (attachment.toReplace) this.replaceAudioRecording(attachment)

      const {toReplace, ...uploadedAttachment} = attachment
      this.encounterAttachments.push(uploadedAttachment)
      this.updateAttachments()
      await this.deleteRecordingSession(sessionId)
      return {status: 'success'}
    },
    // Resumes this encounter's own pending recording session, deterministically recovered from IndexedDB
    async processPendingUploads() {
      if (this.isSyncingPending || !this.isOnline || !this.encounterId) return

      this.isSyncingPending = true

      try {
        // Debounce reconnect bursts
        await sleep(5000)

        if (!this.isOnline) return

        const pendingSession = await this.claimPendingSession(this.encounterId)
        if (!pendingSession) return

        await this.$store.dispatch('encounters/syncingModal', true)
        this.$store.dispatch('notify', {value: 'Syncing offline recording...', color: 'info'})

        try {
          await this.finalizeRecordingUpload(pendingSession)
          this.$store.dispatch('notify', {
            value: 'Offline recording uploaded successfully.',
            color: 'success'
          })
        } catch (error) {
          // Release the claim so the next reconnect can attempt
          await this.releasePendingSession(pendingSession.sessionId)
          this.$store.dispatch('notify', {
            value: 'Failed to upload offline recording. Will retry on next reconnect.',
            color: 'error'
          })
        } finally {
          await this.$store.dispatch('encounters/syncingModal', false)
        }
      } finally {
        this.isSyncingPending = false
      }
    },
    // Reads the pending session and marks it 'uploading' in the same IndexedDB record
    async claimPendingSession(sessionId) {
      const meta = await encounterRecordings.getItem(this.sessionMetaKey(sessionId))
      if (!meta || meta.status !== 'pending_upload') return null

      await encounterRecordings.setItem(this.sessionMetaKey(sessionId), { ...meta, status: 'uploading' })

      return {
        sessionId: meta.sessionId,
        chunksSent: meta.chunksSent,
        recordingMimeType: meta.recordingMimeType,
        attachment: meta.attachment,
      }
    },
    async releasePendingSession(sessionId) {
      const meta = await encounterRecordings.getItem(this.sessionMetaKey(sessionId))
      if (meta) await encounterRecordings.setItem(this.sessionMetaKey(sessionId), { ...meta, status: 'pending_upload' })
    },
    // Persists the stopped recording's session data into the existing meta-record
    async savePendingUpload(sessionData) {
      const existingMeta = (await encounterRecordings.getItem(this.sessionMetaKey(sessionData.sessionId))) || {}

      await encounterRecordings.setItem(this.sessionMetaKey(sessionData.sessionId), {
        ...existingMeta,
        sessionId: sessionData.sessionId,
        status: 'pending_upload',
        chunksSent: sessionData.chunksSent,
        recordingMimeType: sessionData.recordingMimeType,
        attachment: sessionData.attachment,
        updatedAt: Date.now(),
      })
    },
    sessionMetaKey(sessionId) {
      return `${sessionId}::meta`
    },
    chunkKey(sessionId, index) {
      return `${sessionId}::chunk::${String(index).padStart(6, '0')}`
    },
    async persistChunk(blob, index) {
      try {
        const key = await getEncryptionKey()
        const buffer = await blob.arrayBuffer()
        const { iv, data } = await encryptBytes(key, buffer)
        await encounterRecordings.setItem(this.chunkKey(this.encounterId, index), { iv, data })
      } catch (e) {
        // chunksSent was already incremented for this index, so this leaves a gap
        // that surfaces later as a stitch error
        this.recordingIncomplete = true
        this.$store.dispatch('notify', { value: 'Failed to save part of the recording on device.', color: 'error' })
      }
    },
    async stitchSessionChunks(sessionId, count, mimeType) {
      const key = await getEncryptionKey()
      const parts = []
      for (let i = 0; i < count; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        const record = await encounterRecordings.getItem(this.chunkKey(sessionId, i))
        if (!record) throw new Error(`Missing recording chunk ${i}`)
        // eslint-disable-next-line no-await-in-loop
        const buffer = await decryptBytes(key, record.iv, record.data)
        parts.push(buffer)
      }

      return new Blob(parts, { type: mimeType })
    },
    async deleteRecordingSession(sessionId) {
      const keys = await encounterRecordings.keys()
      await Promise.all(
        keys.filter(key => key.startsWith(`${sessionId}::`)).map(key => encounterRecordings.removeItem(key)),
      )
    },
    downloadAudioAttachment(item) {
      this.$store.dispatch('encounters/syncingModal', !item.renewUrl)
      this.$store.dispatch('patients/getPresignedUrl', {
        urlString: item.attachment_filepath,
        method: 'GetObject',
        seconds: 600,
      })
        .then(response => {
          if (response.status === 'success') {
            const attachmentIdx = this.encounterAttachments.findIndex(attachment => attachment.id === item.id)
            this.encounterAttachments[attachmentIdx].data = response.data

            this.$store.dispatch('encounters/syncingModal', false)
            if (!item.renewUrl) {
              this.$store.dispatch('notify', { value: 'Audio file finished downloading!', color: 'success' })
            }
          } else {
            this.$store.dispatch('encounters/syncingModal', false)
            this.$store.dispatch('notify', { value: 'Unable to download attachment', color: 'error' })
          }
        })
        .catch(e => Vue.prototype.$custom.processCommError(e, 'get presigned url'))
    },
    currentAttachmentsArr() {
      const attachments = this.downloadedAttachments.length
        ? this.downloadedAttachments
        : this.encounterAttachments

      // Filter attachments by the current dialogAttachmentType (which should be the 'visit-dictation' bucket)
      return attachments.filter(item => item.attachment_type === this.dialogAttachmentType && item.encounter_id === this.encounterId)
    },
    beginAIProcessing() {
      this.$root.confirm({
        titleIconColor: 'warning',
        title: 'Begin AI audio processing?',
        body: 'Proceeding will commence with sending the current audio file to be processed, continue?',
        cancel: 'No',
        confirm: 'Yes',
      }).then(result => {
        if (result) {
          this.getPresignedUrlAndBeginAIProcessing()
        }

        return false
      })
    },
    async getPresignedUrlAndBeginAIProcessing() {
      const audioAttachment = this.currentAttachmentsArr()[0]

      this.$store.dispatch('encounters/syncingModal', true)
      this.$store.dispatch('patients/getPresignedUrl', {
        urlString: audioAttachment.attachment_filepath,
        method: 'GetObject',
        seconds: 1800,
      })
        .then(response => {
          if (response.status === 'success') {
            this.$store.dispatch('patients/initiateAIAudioProcessing', {
              s3Url: response.data,
              filename: audioAttachment.attachment_filename,
              retries: 3,
            }).then(aiResponse => {
              if (aiResponse.status === 'PROCESSING') {
                const jobId = aiResponse.id
                this.$store.dispatch('patients/pollAIJobStatus', {
                  jobId,
                }).then(completeRes => {
                  if (completeRes.status === 'COMPLETED') {
                    this.$store.dispatch('encounters/syncingModal', false)
                    this.$store.dispatch('notify', { value: 'AI transcribing processing successful', color: 'success' })

                    // Remove comment below when integrating API
                    this.$emit('open-wound-mapping-modal', true)
                    this.modalState = false
                  } else {
                    this.$store.dispatch('encounters/syncingModal', false)
                    this.$store.dispatch('notify', { value: 'AI transcribing processing of audio file failed', color: 'error' })
                  }
                }).catch(e => Vue.prototype.$custom.processCommError(e, 'error in AI transcription'))
              } else {
                this.$store.dispatch('encounters/syncingModal', false)
                this.$store.dispatch('notify', { value: 'AI processing of audio file failed', color: 'error' })
              }
            }).catch(e => Vue.prototype.$custom.processCommError(e, 'begin audio processing: ai processing error'))
          } else {
            this.$store.dispatch('encounters/syncingModal', false)
            this.$store.dispatch('notify', { value: 'Unable to retrieve presigned url for AI processing', color: 'error' })
          }
        })
        .catch(e => Vue.prototype.$custom.processCommError(e, 'begin audio processing: get presigned url error'))
    },
    replaceAttachmentDialog() {
      this.$root.confirm({
        title: 'Replace existing audio file?',
        body: 'Are you sure you want to proceed?',
        cancel: 'Cancel',
        confirm: 'Replace Audio',
        confirmColor: 'error',
      }).then(result => {
        if (result) {
          this.$refs.uploader.click()
        }

        return false
      })
    },
    replaceAudioRecording(attachment) {
      for (let i = this.encounterAttachments.length - 1; i >= 0; i -= 1) {
        const existing = this.encounterAttachments[i]
        if (existing.attachment_type === attachment.attachment_type
          && existing.encounter_id === attachment.encounter_id) {
          this.encounterAttachments.splice(i, 1)
        }
      }
      attachment.replaced = true
      attachment.updated = true
      attachment.data = 'placeholder'
    },
    resetUploadValues() {
      this.file = []
      this.fileDescription = null
      this.isFileSelected = false
      this.attachment = {
        attachment_filename: 'dictation',
        attachment_mime_type: null,
        attachment_filepath: '',
        attachment_width: 0,
        attachment_height: 0,
        s3_upload_status: '',
        data: null,
      }
    },
    async closeModal() {
      // Closing mid-recording leaves MediaRecorder running, the mic capturing, and chunks
      // accumulating in iDB until the 24h cleanup
      if (this.isRecording) {
        const confirmed = await this.$root.confirm({
          title: 'Stop the recording in progress?',
          body: 'Closing will stop and upload the current recording. Are you sure you want to proceed?',
          cancel: 'Keep Recording',
          confirm: 'Stop Recording',
          confirmColor: 'error',
        })

        if (!confirmed) return

        await this.stopRecording()
      }
      this.modalState = false
      this.dialogAttachmentType = null
      this.$emit('update:audioRecorderModal', this.modalState)
      this.$emit('update:attachmentType', this.dialogAttachmentType)
      this.resetUploadValues()
    },
    mimeTypeToExtension(mimeType) {
      const mimeTypeMap = {
        'audio/x-m4a': '.m4a',
        'audio/m4a': '.m4a',
        'audio/mp4': '.m4a',
        'audio/webm': '.webm',
        'audio/aac': '.aac',
        'audio/mpeg': '.mp3',
        'audio/mp3': '.mp3',
        'audio/wav': '.wav',
        'audio/x-wav': '.wav',
        'audio/ogg': '.ogg',
        'audio/flac': '.flac',
        'audio/x-flac': '.flac',
        'audio/x-aiff': '.aiff',
        'audio/aiff': '.aiff',
      }

      return mimeTypeMap[mimeType] || null
    },
    validateCreateUrl(attachment) {
      const { id } = attachment
      const filename = attachment.attachment_filename
      const mimeType = this.mimeTypeToExtension(attachment.attachment_mime_type)

      if (!id) {
        throw new Error('Attachment ID is missing.')
      }
      if (!filename) {
        throw new Error('Attachment filename is missing.')
      }
      if (!mimeType) {
        throw new Error(`Unknown MIME type: ${attachment.attachment_mime_type}.`)
      }

      return `${id}/${filename}${mimeType}`
    },
  },
}
</script>

<style lang="scss" scoped>
.audio-modal {
  .table-preview {
    width: 100%;
    height: 75px;
    line-height: 75px;
  }
}
::v-deep {
  .image-preview {
    height: 46px;
    line-height: 46px;
    width: auto;
    border: 1px solid #aaa;
    border-radius: 5px;
    font-size: 1rem;
    &.mime-type {
      padding: 0 5px;
    }
  }
  .v-btn.v-btn--disabled {
    &.v-btn--active {
      background-color: var(--v-secondary-lighten3) !important;
      .v-btn__content {
        color: rgba(0, 0, 0, 0.42);
      }
    }
  }
  .v-data-table.disable {
    position: relative;
    .v-data-table__wrapper:before {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      background-color: map-get($shades, 'white');
      z-index: 10;
      opacity: 0.5;
    }
  }
}
</style>
